/**
 * SignupPage
 * ──────────
 * Renders <BusinessSignup> for any manifest, identified by :manifestId.
 * Supports an optional ?ref=directory_slug&country=au query string for
 * directory-prefill (e.g. when a venue claims their existing listing
 * and continues into a module signup).
 */
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import BusinessSignup from "@/components/BusinessSignup";
import { getManifest } from "@/lib/businessSignupManifests";

export default function SignupPage() {
  const { manifestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const manifest = getManifest(manifestId);

  const directoryId = searchParams.get("ref") || searchParams.get("directory_id");
  const country = searchParams.get("country") || manifest?.country || "au";

  if (!manifest) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6" data-testid="signup-unknown">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-red-500 mb-3" />
          <h1 className="text-lg font-bold text-stone-900">Unknown signup flow</h1>
          <p className="text-[13px] text-stone-600 mt-1">
            <code>{manifestId}</code> isn&apos;t a registered manifest.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-1 mt-4 text-[13px] font-bold text-amber-700 hover:underline"
            data-testid="signup-back-hub"
          >
            <ArrowLeft className="w-4 h-4" /> Back to signup hub
          </Link>
        </div>
      </div>
    );
  }

  const prefill = directoryId ? { directory_id: directoryId, country } : null;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/signup"
            className="inline-flex items-center gap-1 text-[12px] font-bold text-stone-600 hover:text-stone-900"
            data-testid="signup-back-hub"
          >
            <ArrowLeft className="w-4 h-4" /> All signup flows
          </Link>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
            HustlePlaza · {manifest.module}
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <BusinessSignup
          manifestId={manifestId}
          prefill={prefill}
          onSubmit={(result) => {
            // Stash the directory_id so consumer pages can link to the
            // freshly-created listing. Stay on this page so the success
            // card renders.
            if (result?.directory_id && typeof window !== "undefined") {
              try {
                window.sessionStorage.setItem("hp_last_signup_directory_id", result.directory_id);
              } catch (_) {
                // ignore quota / privacy errors
              }
            }
          }}
        />
      </main>
    </div>
  );
}
